import { useEffect, useState } from "react";
import { Star, ChevronDown } from "lucide-react";
import api from "@/api/client";
import VerifiedBadge from "@/components/VerifiedBadge";

function Stars({ value = 0 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3 w-3 ${i <= Math.round(value) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
      ))}
    </span>
  );
}

function ReviewSection({ label, endpoint, testId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    if (!open && !data) {
      setLoading(true);
      try { const r = await api.get(endpoint); setData(r.data); } catch { setData({ reviews: [], count: 0, average: 0 }); }
      finally { setLoading(false); }
    }
    setOpen((v) => !v);
  };
  return (
    <div className="mt-2">
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={toggle}
        className="w-full flex items-center justify-between text-left text-xs px-2.5 py-1.5 rounded-full bg-gray-50 border border-gray-200 hover:border-[#F48E2E] hover:text-[#F48E2E]"
      >
        <span>{label}{data ? ` (${data.count || 0})` : ""}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={testId} className="mt-1.5 rounded-xl border border-gray-100 bg-white p-2 space-y-1.5 max-h-56 overflow-y-auto">
          {loading && <div className="text-xs text-gray-500">Loading…</div>}
          {!loading && data && data.reviews && data.reviews.length === 0 && (
            <div className="text-xs text-gray-500">No reviews yet.</div>
          )}
          {!loading && data && (data.reviews || []).map((r, idx) => (
            <div key={r.id || idx} className="text-xs text-gray-700 border-b border-gray-100 pb-1.5 last:border-0 last:pb-0">
              <div className="flex items-center gap-1.5">
                <Stars value={r.rating} />
                <span className="font-medium">{r.author || "Verified buyer"}</span>
                {r.product_name && <span className="text-gray-400 truncate max-w-[140px]">· {r.product_name}</span>}
              </div>
              <p className="mt-0.5 leading-snug text-gray-600">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrderSummaryItem({ item, product, size }) {
  const [expandedDesc, setExpandedDesc] = useState(false);
  if (!product) {
    return (
      <div className="flex gap-3 py-3 border-b border-gray-100 last:border-0 animate-pulse">
        <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-100 rounded w-2/3" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      </div>
    );
  }
  const desc = product.description || "";
  const truncated = desc.length > 120 && !expandedDesc;
  const shownDesc = truncated ? desc.slice(0, 120).trimEnd() + "…" : desc;

  return (
    <div className="flex gap-3 py-3 border-b border-gray-100 last:border-0" data-testid={`summary-item-${product.id}`}>
      <img src={product.image_base64} alt={product.name} className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" data-testid={`summary-item-name-${product.id}`}>{product.name}</div>
            <div className="text-[11px] text-gray-500 font-mono-tab" data-testid={`summary-item-code-${product.id}`}>{product.product_code || `CS-${product.id.split("-")[0].toUpperCase()}`}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium whitespace-nowrap">₹{(item.price * item.quantity).toLocaleString("en-IN")}</div>
            <div className="text-[11px] text-gray-500">× {item.quantity}{size ? ` · ${size}` : ""}</div>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500 flex-wrap">
          {product.vendor_business_name && (
            <span data-testid={`summary-item-seller-${product.id}`} className="inline-flex items-center gap-1">
              Sold by <span className="text-gray-800 font-medium">{product.vendor_business_name}</span>
              {product.vendor_verified && <VerifiedBadge size="h-3.5 w-3.5" testid={`summary-item-verified-${product.id}`} />}
            </span>
          )}
          <span className="inline-flex items-center gap-1" data-testid={`summary-item-rating-${product.id}`}>
            <Stars value={product.rating || 4.5} />
            <span>{(product.rating || 4.5).toFixed(1)}</span>
          </span>
        </div>
        {desc && (
          <p className="mt-1.5 text-[11px] text-gray-600 leading-snug" data-testid={`summary-item-desc-${product.id}`}>
            {shownDesc}
            {desc.length > 120 && (
              <button
                type="button"
                onClick={() => setExpandedDesc((v) => !v)}
                className="ml-1 text-[#F48E2E] hover:underline"
              >
                {truncated ? "Read more" : "Show less"}
              </button>
            )}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <div className="flex-1">
            <ReviewSection
              label="Product reviews"
              endpoint={`/reviews/product/${product.id}`}
              testId={`product-reviews-${product.id}`}
            />
          </div>
          {product.vendor_id && (
            <div className="flex-1">
              <ReviewSection
                label="Seller reviews"
                endpoint={`/reviews/vendor/${product.vendor_id}`}
                testId={`vendor-reviews-${product.id}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
